import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';

const Header = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeSection, setActiveSection] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const navigationItems = [
    {
      id: 'who-are-we',
      label: 'Who are we',
      labelAr: 'من نحن',
      labelTr: 'Biz kimiz',
      description: 'About Us',
      descriptionAr: 'من نحن',
      descriptionTr: 'Hakkımızda'
    },
    {
      id: 'get-involved',
      label: 'Register',
      labelAr: 'سجل للمشاركة',
      labelTr: 'Kaydol',
      description: 'Register to participate',
      descriptionAr: 'سجل للمشاركة',
      descriptionTr: 'Katılmak için kaydolun'
    },
    {
      id: 'event-program',
      label: 'Program',
      labelAr: 'برنامج الحفل',
      labelTr: 'Program',
      description: 'Ceremony Program',
      descriptionAr: 'برنامج الحفل',
      descriptionTr: 'Tören Programı'
    },
    {
      id: 'guests',
      label: 'Guests',
      labelAr: 'الضيوف',
      labelTr: 'Konuklar',
      description: 'Event Guests',
      descriptionAr: 'ضيوف الحفل',
      descriptionTr: 'Etkinlik Konukları'
    },
    {
      id: 'sponsors',
      label: 'Sponsor',
      labelAr: 'الرعاة',
      labelTr: 'Sponsor',
      description: 'Our Sponsors',
      descriptionAr: 'رعاتنا',
      descriptionTr: 'Sponsorlarımız'
    }
  ];

  const memorialTitle = {
    ar: 'ذكرى تحرير سوريا',
    en: 'Anniversary of the liberation of Syria',
    tr: 'Suriye\'nin kurtuluş yıldönümü'
  };

  const memorialDate = {
    ar: '٨ ديسمبر ٢٠٢٤',
    en: 'December 8, 2024',
    tr: '8 Aralık 2024'
  };

  const ctaText = {
    ar: 'سجِّل الآن',
    en: 'Register Now',
    tr: 'Şimdi Kayıt Ol'
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const scrollDifference = currentScrollY - lastScrollY;
          
          setIsScrolled(currentScrollY > 20);

          // Show header at top of page or when scrolling up
          // Hide header when scrolling down (but only after scrolling past a threshold)
          if (currentScrollY < 50) {
            // Always show header near the top
            setIsHeaderVisible(true);
          } else if (Math.abs(scrollDifference) > 5) {
            // Only update if scroll difference is significant enough (reduces flickering)
            if (scrollDifference > 0 && currentScrollY > 50) {
              // Scrolling down - hide header
              setIsHeaderVisible(false);
            } else if (scrollDifference < 0) {
              // Scrolling up - show header
              setIsHeaderVisible(true);
            }
          }

          setLastScrollY(currentScrollY);

          // Find active section based on scroll position
          const scrollPosition = currentScrollY + 200;
          const sections = navigationItems.map(item => ({
            id: item.id,
            element: document.getElementById(item.id),
            offset: document.getElementById(item.id)?.offsetTop || 0
          }));

          const currentSection = sections
            .filter(section => section.element && scrollPosition >= section.offset)
            .pop();

          if (currentSection && !isNavigating) {
            setActiveSection(currentSection.id);
          } else if (!currentSection && currentScrollY < 100 && !isNavigating) {
            // Reset to empty when at top
            setActiveSection('');
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isNavigating, lastScrollY]);

  // Handle body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      // Store the current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Restore body scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    }

    // Cleanup function
    return () => {
      if (!isNavigating) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
      }
    };
  }, [isMobileMenuOpen, isNavigating]);

  const handleNavClick = (sectionId) => {
    // Set navigating flag to prevent scroll restoration
    setIsNavigating(true);
    
    // Show header when navigating
    setIsHeaderVisible(true);

    // Immediately set the target section to prevent flickering
    setActiveSection(sectionId);

    // Close mobile menu first
    setIsMobileMenuOpen(false);

    // Wait for menu animation and body scroll unlock
    setTimeout(() => {
      if (sectionId === 'hero') {
        // Scroll to top for hero section
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          // Calculate scroll position with offset to position title higher
          const headerHeight = window.innerWidth >= 1024 ? 128 : 96; // lg:h-32 (128px) or h-24 (96px)
          const additionalOffset = 40; // Extra space to show title higher
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerHeight - additionalOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
      // Reset navigating flag after navigation completes
      setTimeout(() => setIsNavigating(false), 1000);
    }, 100);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getLabel = (item) => {
    switch (currentLanguage) {
      case 'ar': return item?.labelAr;
      case 'tr': return item?.labelTr;
      default: return item?.label;
    }
  };

  const getDescription = (item) => {
    switch (currentLanguage) {
      case 'ar': return item?.descriptionAr;
      case 'tr': return item?.descriptionTr;
      default: return item?.description;
    }
  };

  const isRTL = currentLanguage === 'ar';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-scroll-navigation transition-transform duration-500 ease-in-out ${
          isHeaderVisible || isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
        } ${isScrolled
          ? 'bg-background/95 backdrop-blur-sm shadow-memorial border-b border-border'
          : 'bg-white/30 backdrop-blur-[1px]'
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-center lg:justify-between h-24 lg:h-32">
            {/* Mobile Menu Button - Left side for all languages */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden absolute left-0 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md p-2 text-text-secondary hover:text-primary memorial-transition"
              aria-label="Toggle mobile menu"
            >
              <Icon
                name={isMobileMenuOpen ? "X" : "Menu"}
                size={24}
              />
            </button>

            {/* Logo */}
            <div className="flex-shrink-0">
              <button
                onClick={() => handleNavClick('hero')}
                className="flex items-center gap-2 sm:gap-3 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg p-0"
              >
                <Image
                  src="/assets/logos/2-01.png"
                  alt="Anniversary of the liberation of Syria Logo"
                  className="h-16 sm:h-20 lg:h-28 w-auto object-contain"
                />
                <Image
                  src="/assets/logos/01.png"
                  alt="Liberation Day Logo"
                  className="h-16 sm:h-20 lg:h-28 w-auto object-contain"
                />
              </button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-2 lg:ms-12">
              {navigationItems?.map((item) => (
                <button
                  key={item?.id}
                  onClick={() => handleNavClick(item?.id)}
                  className="font-body text-base px-5 py-2.5 rounded-md memorial-transition text-text-primary bg-white/90 hover:text-primary hover:bg-white shadow-sm"
                >
                  {getLabel(item)}
                </button>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-5 lg:ms-12">
              <Button
                variant="default"
                size="lg"
                onClick={() => handleNavClick('get-involved')}
                className="cta-button text-lg"
              >
                {ctaText?.[currentLanguage]}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-modal lg:hidden">
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className={`fixed top-0 ${isRTL ? 'left-0' : 'right-0'} w-full max-w-sm h-full bg-background shadow-xl overflow-y-auto ${isRTL ? 'animate-slide-in-left' : 'animate-slide-in-right'
            }`}>
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <Image
                  src="/assets/logos/2-01.png"
                  alt="Anniversary of the liberation of Syria Logo"
                  className="h-20 w-auto object-contain"
                />
                <Image
                  src="/assets/logos/01.png"
                  alt="Liberation Day Logo"
                  className="h-20 w-auto object-contain"
                />
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md p-2 text-text-secondary hover:text-primary memorial-transition"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <nav className="px-6 py-4 space-y-1">
              {navigationItems?.map((item) => (
                <button
                  key={item?.id}
                  onClick={() => handleNavClick(item?.id)}
                  className={`w-full ${isRTL ? 'text-right' : 'text-left'} px-4 py-4 rounded-lg transition-colors duration-250 min-h-[56px] text-text-secondary hover:text-primary hover:bg-muted`}
                >
                  <div className="font-medium text-lg">{getLabel(item)}</div>
                  <div className="text-base text-text-secondary mt-1">{getDescription(item)}</div>
                </button>
              ))}
            </nav>

            <div className="px-6 py-4 border-t border-border mt-4">
              <Button
                variant="default"
                fullWidth
                onClick={() => handleNavClick('get-involved')}
                className="cta-button text-lg"
              >
                {ctaText?.[currentLanguage]}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
